import { cva, type VariantProps } from 'class-variance-authority';
import { CircleCheck, Info, X } from 'lucide-react';
import type { ComponentProps, ComponentType } from 'react';

import { type ClassValue, clsxm } from '@vs-shop/utils';

import { IconExclamationCircle, IconExclamationTriangle } from '../../icons/index.ts';

/**
 * The surface a mutation reports itself on:
 *
 *   <Toast variant={ToastVariant.SUCCESS} onClose={dismiss}>
 *     Produkt byl přidán do košíku.
 *   </Toast>
 *
 * A ground, a mark, the message and the way to dismiss it. What produced it,
 * where it is stacked, how long it stays and what happens on a click are all
 * the notifier's — `./toastNotifier.tsx` — and none of them reach this file.
 *
 * **Not a compound.** Every other part in this tier opens its anatomy because a
 * composition writes it; a toast is never written. It is produced imperatively
 * by `showSuccessToast` and friends, so there is no call site to open the
 * anatomy FOR, and the four slots below are internal for the same reason
 * `Alert`'s and `Notice`'s are.
 *
 * It lives in the primitive tier because it carries no commerce meaning: a
 * coloured bar saying a thing worked is the same bar whether the thing was a
 * basket or a password.
 *
 * Server-first: a plain component. The handler a notifier hangs on `onClose`
 * makes the CALLER a client file, which the notifier already is.
 */

/**
 * How loud the toast is, and what colour says so.
 *
 * Four arms and no neutral one: a toast exists because something happened, and
 * "something happened, no idea what" is not a state this surface has. The
 * grounds are the platform's status colours — `error` paints `destructive`,
 * because the arm is named for react-toastify's own vocabulary (the notifier
 * passes the same string as its `type`, which is what the tenant progress-bar
 * skin keys off) while the token is named for the platform's.
 */
const toastVariants = cva('flex items-center gap-2 rounded-lg p-4', {
  variants: {
    variant: {
      /** It worked. */
      success: 'bg-success text-success-foreground',
      /** It did not. */
      error: 'bg-destructive text-destructive-foreground',
      /** It worked, but not the way the shopper asked. */
      warning: 'bg-warning text-warning-foreground',
      /** Nothing happened; here is something to know. */
      info: 'bg-info text-info-foreground',
    },
  },
});

type ToastVariantName = NonNullable<VariantProps<typeof toastVariants>['variant']>;

export type ToastVariant = ToastVariantName;

/**
 * The appearance axis, derived from the recipe above and bound to it by
 * `satisfies`, so the union and the recipe cannot drift.
 */
export const ToastVariant = {
  /** It worked. */
  SUCCESS: 'success',
  /** It did not. */
  ERROR: 'error',
  /** It worked, but not the way the shopper asked. */
  WARNING: 'warning',
  /** Nothing happened; here is something to know. */
  INFO: 'info',
} as const satisfies Record<string, ToastVariantName>;

/**
 * The mark each arm carries. Two are drawn by lucide and two by this repo's own
 * set: a swap is a visual change, so each arm keeps the glyph it renders today.
 */
const TOAST_MARKS = {
  success: CircleCheck,
  error: IconExclamationTriangle,
  warning: IconExclamationCircle,
  info: Info,
} as const satisfies Record<ToastVariant, ComponentType<ComponentProps<'svg'>>>;

/**
 * `onClose` is taken off the native surface and re-declared: React's
 * `DOMAttributes` gives every element an `onClose` event handler, and this one
 * is not that event — it is what the dismiss control calls.
 */
export type ToastProps = Omit<ComponentProps<'div'>, 'className' | 'onClose'> & {
  /** Required and closed — not `VariantProps`, which makes it optional. */
  readonly variant: ToastVariant;
  readonly className?: ClassValue;
  /** What dismissing the toast does. Absent leaves the control inert. */
  readonly onClose?: () => void;
  /**
   * The dismiss control's accessible name. Defaults to the English word the
   * surface has shipped with; a tenant that localizes its chrome passes its
   * own.
   */
  readonly closeLabel?: string;
};

/** One toast. */
export const Toast = ({
  variant,
  className,
  onClose,
  closeLabel = 'Close',
  children,
  ...props
}: ToastProps) => {
  const Mark = TOAST_MARKS[variant];

  return (
    <div
      data-slot="toast"
      data-toast-variant={variant}
      className={clsxm(toastVariants({ variant }), className)}
      role="status"
      aria-live="polite"
      {...props}
    >
      <div data-slot="toast-body" className="flex gap-2">
        <span
          data-slot="toast-icon"
          className="flex size-6 items-center justify-center"
          aria-hidden="true"
        >
          <Mark className="size-5 shrink-0" />
        </span>

        <span data-slot="toast-message" className="flex-1 text-lg/6">
          {children}
        </span>
      </div>

      {/* `ml-auto` and not the container's `justify-between`: the control
          anchors itself to the far end, so it lands there whatever else the
          row happens to hold. */}
      <button
        type="button"
        data-slot="toast-close"
        onClick={onClose}
        aria-label={closeLabel}
        className="ml-auto p-1"
      >
        <X className="size-5 shrink-0" />
      </button>
    </div>
  );
};
